// Buy nodeStream credits: the card's pure pricing and delivery logic. It mirrors the lambdas' _core/public-ledger/credits.ts,
// so the card shows the same figures with no fetch; the server always recomputes them and decides.
import { formatCredits } from './public-ledger-credits.js';

// A purchase is at least 10,000 credits (100 of the ledger's unit), with no maximum. The server's figure, which the quote
// carries (purchaseMinimum), wins: this is only used until the quote arrives
export const MIN_CREDITS = 10000;
// The price is five times our cost: 0.01 of the ledger's unit per credit, at the current mainnet rate
export const PRICE_MULTIPLE = 5;
export const LEDGER_UNIT_PER_CREDIT = 0.01;
// A quote holds its price for 30 minutes
export const QUOTE_MINUTES = 30;

export const PURCHASE_TESTNET_ONLY_CODE = 'PUBLIC_LEDGER_PURCHASE_TESTNET_ONLY';
export const PURCHASE_TESTNET_ONLY_MESSAGE =
  'Purchases are only available on testnet outside production. Ask the PUBLIC_LEDGER team to fund this test company.';

// #41 (Design 3): credits are bought by hand only once the company's automatic payments are on (the server's
// credits-launch.ts AUTOPAY_NEEDED_CODE and its message, word for word)
export const PURCHASE_AUTOPAY_NEEDED_CODE = 'PUBLIC_LEDGER_PURCHASE_AUTOPAY_NEEDED';
export const PURCHASE_AUTOPAY_NEEDED_MESSAGE =
  'Your company needs automatic payments on to buy nodeStream credits. A signatory can set them up from My Account.';

export interface CreditQuote {
  id: string;
  unitPricePence: number;
  expiresAt: string | Date;
}

export type CreditPurchaseStatus =
  | 'QUOTED'
  | 'PAYMENT_PENDING'
  | 'PAID'
  | 'CREDITS_TRANSFERRED'
  | 'FULLY_DELIVERED'
  | 'TRANSFER_FAILED'
  | 'REFUNDED';

// The fields of a plCreditPurchase the user policy lets a company's members read
export interface CreditPurchaseProgress {
  status?: CreditPurchaseStatus | string | null;
  creditsBought?: number | null;
  creditsDelivered?: number | null;
  creditsOwed?: number | null;
}

/** The minimum purchase the quote names (the environment store's credits.minCredits), else MIN_CREDITS. */
export function purchaseMinimum(quote: { minCredits?: number } | null | undefined): number {
  const min = quote?.minCredits;
  return typeof min === 'number' && Number.isSafeInteger(min) && min > 0 ? min : MIN_CREDITS;
}

/**
 * Why an amount of credits can't be bought, or null when it can: a whole number of at least the minimum (10,000 unless the
 * quote names another), with no maximum.
 */
export function validateCredits(credits: number | null | undefined, minCredits: number = MIN_CREDITS): string | null {
  if (credits === null || credits === undefined || !Number.isFinite(credits)) return 'Enter a number of credits.';
  if (!Number.isSafeInteger(credits)) return 'Enter a whole number of credits.';
  if (credits < minCredits) return `The minimum purchase is ${formatCredits(minCredits)} credits.`;
  return null;
}

/**
 * The price of one credit in pence: 5 × 0.01 of the ledger's unit, at `centsPerHbar` US cents per unit and `usdGbp` pounds
 * per dollar. It moves with both rates, so the quote locks it for 30 minutes.
 */
export function unitPricePence(centsPerHbar: number, usdGbp: number): number {
  return PRICE_MULTIPLE * LEDGER_UNIT_PER_CREDIT * centsPerHbar * usdGbp;
}

/**
 * The total in whole pence for `credits` at `unitPence` each, rounded up. The product is first cut to 6 decimals, so a float
 * error (5219.999999…) doesn't add a penny.
 */
export function totalPence(credits: number, unitPence: number): number {
  return Math.ceil(Number((credits * unitPence).toFixed(6)));
}

// A purchase's tinybars: exactly credits × 10^6 (credits ÷ 100 of the ledger's unit), whatever the rate
export function creditsToTinybars(credits: number): bigint {
  return BigInt(credits) * BigInt(1_000_000);
}

// Whether the quote's price still holds at `now`
export function isQuoteLive(quote: Pick<CreditQuote, 'expiresAt'> | null | undefined, now: Date = new Date()): boolean {
  if (!quote?.expiresAt) return false;
  const expires = new Date(quote.expiresAt).getTime();
  return Number.isFinite(expires) && expires > now.getTime();
}

// Whole seconds left on a quote at `now`, never below 0
export function quoteSecondsLeft(expiresAt: string | Date | null | undefined, now: Date = new Date()): number {
  if (!expiresAt) return 0;
  const expires = new Date(expiresAt).getTime();
  if (!Number.isFinite(expires)) return 0;
  return Math.max(0, Math.floor((expires - now.getTime()) / 1000));
}

// A countdown as m:ss
export function formatCountdown(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

// Pence as pounds for people to read: £52.20
export function formatPence(pence: number): string {
  if (!Number.isFinite(pence)) return '–';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100);
}

// A price per credit in pence, to 3 decimals: 0.348p
export function formatUnitPence(pence: number): string {
  if (!Number.isFinite(pence)) return '–';
  return `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 3 }).format(pence)}p`;
}

// Whether a lambda refused because purchases are for TESTNET companies only outside production (concern 2). The server
// decides; the card shows the message in place of the price and payment.
export function isTestnetOnlyRefusal(err: unknown): boolean {
  const text = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  return text.includes(PURCHASE_TESTNET_ONLY_CODE) || text.includes(PURCHASE_TESTNET_ONLY_MESSAGE);
}

// Whether a lambda refused because the company's automatic payments aren't on (#41). The server decides; the card shows the
// message in place of the price and payment, as for the testnet-only refusal
export function isAutopayNeededRefusal(err: unknown): boolean {
  const text = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  return text.includes(PURCHASE_AUTOPAY_NEEDED_CODE) || text.includes(PURCHASE_AUTOPAY_NEEDED_MESSAGE);
}

/**
 * The line a Buy nodeStream credits card shows in place of its price and payment when the server refuses the purchase
 * outright: a MAINNET company outside production (concern 2), or #41 a company whose automatic payments aren't on (a card
 * left open from before). Null for any other refusal, which is shown as an error.
 */
export function purchaseRefusalLine(err: unknown): string | null {
  if (isTestnetOnlyRefusal(err)) return PURCHASE_TESTNET_ONLY_MESSAGE;
  if (isAutopayNeededRefusal(err)) return PURCHASE_AUTOPAY_NEEDED_MESSAGE;
  return null;
}

/**
 * How many of `credits` would arrive now, as public-ledger-credits-transfer shares them (_core/public-ledger/credits.ts
 * deliverableNow): all of them when what nodeStream can send above its reserve covers everything owed, this purchase
 * included; otherwise this purchase's share of it, rounded down. Null when either figure is unknown.
 */
export function deliverableNowCredits(
  credits: number | null,
  availableCredits: number | null | undefined,
  owedCredits: number | null | undefined,
): number | null {
  if (credits === null || availableCredits === null || availableCredits === undefined) return null;
  if (owedCredits === null || owedCredits === undefined) return null;
  if (credits <= 0) return 0;
  const available = BigInt(Math.max(0, Math.floor(availableCredits)));
  const mine = BigInt(Math.floor(credits));
  const all = mine + BigInt(Math.max(0, Math.floor(owedCredits)));
  if (available >= all) return credits;
  return Number((available * mine) / all);
}

/**
 * The card's message when not everything can be delivered now: the credits that arrive now, and that the rest follows.
 * Null when everything arrives now (or nothing is known about what can be delivered).
 */
export function partialDeliveryMessage(
  credits: number | null,
  deliverableNow: number | null | undefined,
): string | null {
  if (credits === null || deliverableNow === null || deliverableNow === undefined) return null;
  if (deliverableNow >= credits) return null;
  const now = Math.max(0, Math.floor(deliverableNow));
  return (
    `${formatCredits(now)} credits will arrive now. nodeStream's team has been notified, and the remaining ` +
    `${formatCredits(credits - now)} credits will follow.`
  );
}

// A purchase whose credits still count: a REFUNDED one (its creditsOwed is 0) and one never paid are left out
const counts = (p: CreditPurchaseProgress) =>
  p.status !== 'REFUNDED' && p.status !== 'QUOTED' && p.status !== 'PAYMENT_PENDING';

export interface CreditsOnTheWay {
  bought: number;
  delivered: number;
  owed: number;
}

// The company's purchases summed, for the "bought · delivered · on the way" line; null when nothing is owed
export function creditsOnTheWay(purchases: CreditPurchaseProgress[]): CreditsOnTheWay | null {
  const owing = purchases.filter((p) => counts(p) && (p.creditsOwed ?? 0) > 0);
  if (owing.length === 0) return null;
  return owing.reduce<CreditsOnTheWay>(
    (sum, p) => ({
      bought: sum.bought + (p.creditsBought ?? 0),
      delivered: sum.delivered + (p.creditsDelivered ?? 0),
      owed: sum.owed + (p.creditsOwed ?? 0),
    }),
    { bought: 0, delivered: 0, owed: 0 },
  );
}

// "20,000 bought · 8,000 delivered · 12,000 on the way"
export function formatCreditsOnTheWay(figures: CreditsOnTheWay): string {
  return `${formatCredits(figures.bought)} bought · ${formatCredits(figures.delivered)} delivered · ${formatCredits(figures.owed)} on the way`;
}

// The purchase card's progress after payment
export function purchaseProgressMessage(purchase: CreditPurchaseProgress | null | undefined): string | null {
  if (!purchase) return null;
  switch (purchase.status) {
    case 'PAYMENT_PENDING':
      return 'Waiting for the payment to be confirmed.';
    case 'PAID':
      return 'Payment received. Your credits are being added to your company’s public ledger account.';
    case 'TRANSFER_FAILED':
      return 'Payment received. Your credits are taking longer than usual to arrive; nodeStream will keep trying.';
    case 'REFUNDED':
      return 'This purchase was refunded.';
    case 'CREDITS_TRANSFERRED':
    case 'FULLY_DELIVERED': {
      const owed = purchase.creditsOwed ?? 0;
      if (owed > 0) {
        return formatCreditsOnTheWay({
          bought: purchase.creditsBought ?? 0,
          delivered: purchase.creditsDelivered ?? 0,
          owed,
        });
      }
      return 'Credits added to your company’s public ledger account.';
    }
    default:
      return null;
  }
}

type Metadata = { key: string; value: string | null }[] | null | undefined;

const metadataValue = (metadata: Metadata, key: string) => metadata?.find((m) => m.key === key)?.value ?? null;

// A non-negative number of credits as a lambda writes it to metadata (a balance can have decimals), or null
function metadataCredits(metadata: Metadata, key: string): number | null {
  const text = metadataValue(metadata, key)?.trim() ?? '';
  return /^\d+(\.\d+)?$/.test(text) ? Number(text) : null;
}

export interface CreditTransferFailedFigures {
  purchaseId: string | null;
  companyId: string | null;
  creditsOwed: number | null;
  lastError: string | null;
}

// The failed-transfer card's figures, from the job metadata public-ledger-credits-transfer writes when it raises the card
export function creditTransferFailedFigures(metadata: Metadata): CreditTransferFailedFigures {
  return {
    purchaseId: metadataValue(metadata, 'PURCHASE_ID'),
    companyId: metadataValue(metadata, 'COMPANY_ID'),
    creditsOwed: metadataCredits(metadata, 'CREDITS_OWED'),
    lastError: metadataValue(metadata, 'LAST_ERROR'),
  };
}

export interface CreditExtraChargeFigures {
  purchaseId: string | null;
  companyId: string | null;
  paymentIntentId: string | null;
  amountPence: number | null;
}

// The extra-charge card's figures, from the job metadata the payment-intent webhook writes when it raises the card
export function creditExtraChargeFigures(metadata: Metadata): CreditExtraChargeFigures {
  const amount = metadataValue(metadata, 'AMOUNT_PENCE')?.trim() ?? '';
  return {
    purchaseId: metadataValue(metadata, 'PURCHASE_ID'),
    companyId: metadataValue(metadata, 'COMPANY_ID'),
    paymentIntentId: metadataValue(metadata, 'PAYMENT_INTENT_ID'),
    amountPence: /^\d+$/.test(amount) ? Number(amount) : null,
  };
}

export interface FundTestCompanyFigures {
  companyId: string | null;
  network: string | null;
  accountId: string | null;
  fundedCredits: number | null;
  balanceAfterCredits: number | null;
}

// The Fund this test company by hand card's figures: what public-ledger-credits-funding-mode wrote when it raised the card,
// and what public-ledger-credits-confirm-test-funding recorded when the team confirmed it
export function fundTestCompanyFigures(metadata: Metadata): FundTestCompanyFigures {
  return {
    companyId: metadataValue(metadata, 'COMPANY_ID'),
    network: metadataValue(metadata, 'NETWORK'),
    accountId: metadataValue(metadata, 'COMPANY_ACCOUNT_ID'),
    fundedCredits: metadataCredits(metadata, 'FUNDED_CREDITS'),
    balanceAfterCredits: metadataCredits(metadata, 'BALANCE_AFTER_CREDITS'),
  };
}

// Why an entered funded amount is refused, or null: a positive whole number of credits
export function validateFundedCredits(credits: number | null | undefined): string | null {
  if (credits === null || credits === undefined || !Number.isSafeInteger(credits) || credits <= 0) {
    return 'Enter the amount sent, as a whole number of credits above 0.';
  }
  return null;
}
